import { Trans, useTranslation } from '@mint-lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  typographyVariants,
} from '@mint-lib/tw-components';

type BulkEditConfirmationDialogProps = {
  headerName: string;
  displayValue: string;
  platformName: string;
  channelName?: string;
  mediaRowName?: string;
  onBulkConfirm: () => void;
  onBulkReject: () => void;
  onClose: () => void;
};

export const BulkEditConfirmationDialog = ({
  headerName,
  displayValue,
  platformName,
  channelName,
  mediaRowName,
  onClose,
  onBulkConfirm,
  onBulkReject,
}: BulkEditConfirmationDialogProps) => {
  const { t } = useTranslation('@myn/import-hub');

  const handleConfirm = () => {
    onBulkConfirm();
    onClose();
  };

  const handleReject = () => {
    onBulkReject();
    onClose();
  };

  const [primaryRule, secondaryRule] =
    headerName === 'channel'
      ? [
          { displayValue, name: t('Channel') },
          { displayValue: mediaRowName, name: t('Media Row name') },
        ]
      : [
          { displayValue, name: t('Media Row name') },
          { displayValue: channelName, name: t('Channel') },
        ];

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot size="s" data-testid="bulk-edit-confirmation-dialog">
          <DialogHeader className="h-auto">
            <DialogTitle>
              {t('Apply the selected {{entityName}} in bulk?', {
                entityName: primaryRule.name,
              })}
            </DialogTitle>
          </DialogHeader>
          <DialogContent className="flex-col">
            <DialogDescription>
              <Trans
                ns="@myn/import-hub"
                i18nKey="Apply <strong>{{displayValue}}</strong> to all rows on <strong>{{platformName}}</strong> where:"
                values={{
                  displayValue: primaryRule.displayValue,
                  platformName,
                }}
                components={{ strong: <strong /> }}
              />
            </DialogDescription>
            <p className={typographyVariants({ variant: 'bodyLong01' })}>
              {secondaryRule.displayValue ? (
                <Trans
                  ns="@myn/import-hub"
                  i18nKey="The {{primaryRuleName}} is colored red (has an error), and the {{secondaryRuleName}} is set to <strong>{{secondaryDisplayValue}}</strong>."
                  values={{
                    primaryRuleName: primaryRule.name,
                    secondaryRuleName: secondaryRule.name,
                    secondaryDisplayValue: secondaryRule.displayValue,
                  }}
                  components={{ strong: <strong /> }}
                />
              ) : (
                <Trans
                  ns="@myn/import-hub"
                  i18nKey="The {{primaryRuleName}} is colored red (has an error), the {{secondaryRuleName}} is colored red (has an error)."
                  values={{
                    primaryRuleName: primaryRule.name,
                    secondaryRuleName: secondaryRule.name,
                  }}
                />
              )}
            </p>
          </DialogContent>
          <DialogFooter>
            <Button
              data-dd-action-name="Bulk edit: only this cell"
              size="large"
              variant="text"
              onClick={handleReject}
            >
              {t('Apply only to this cell')}
            </Button>
            <Button
              data-dd-action-name="Bulk edit: apply to all"
              size="large"
              color="warning"
              onClick={handleConfirm}
            >
              {t('Apply to all')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};
